import { useState, useEffect } from 'react';
import { AdminLayout } from './AdminLayout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { toast } from 'sonner';
import { Plus, Edit2, Trash2, X, Calendar, Clock, MapPin, QrCode, Download } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const defaultOpenHouse = {
  property_address: '',
  city: '',
  state: 'FL',
  zip_code: '',
  date: '',
  start_time: '',
  end_time: '',
  agent_name: '',
  description: '',
  image_url: ''
};

export const AdminOpenHouses = () => {
  const [openHouses, setOpenHouses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingOH, setEditingOH] = useState(null);
  const [formData, setFormData] = useState(defaultOpenHouse);
  const [qrModal, setQrModal] = useState(null);

  useEffect(() => {
    fetchOpenHouses();
  }, []);

  const fetchOpenHouses = async () => {
    try {
      const response = await axios.get(`${API}/open-houses?active_only=false`);
      setOpenHouses(response.data);
    } catch (error) {
      toast.error('Error fetching open houses');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      if (editingOH?.id) {
        await axios.put(`${API}/open-houses/${editingOH.id}`, formData);
        toast.success('Open house updated');
      } else {
        await axios.post(`${API}/open-houses`, formData);
        toast.success('Open house added');
      }
      setEditingOH(null);
      setFormData(defaultOpenHouse);
      fetchOpenHouses();
    } catch (error) {
      toast.error('Error saving open house');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this open house?')) return;
    try {
      await axios.delete(`${API}/open-houses/${id}`);
      toast.success('Open house deleted');
      fetchOpenHouses();
    } catch (error) {
      toast.error('Error deleting open house');
    }
  };

  const openEditModal = (oh = null) => {
    if (oh) {
      setEditingOH(oh);
      setFormData(oh);
    } else {
      setEditingOH({});
      setFormData(defaultOpenHouse);
    }
  };

  const handleGenerateQR = async (oh) => {
    try {
      const response = await axios.get(`${API}/open-houses/${oh.id}/qr-code`);
      setQrModal({
        ...oh,
        qr_code: response.data.qr_code,
        sign_in_url: response.data.url
      });
    } catch (error) {
      toast.error('Error generating QR code');
    }
  };

  const handleDownloadQR = () => {
    if (!qrModal?.qr_code) return;
    
    const link = document.createElement('a');
    link.href = qrModal.qr_code;
    link.download = `qr-code-${qrModal.property_address.replace(/\s+/g, '-')}.png`;
    link.click();
  };

  return (
    <AdminLayout>
      <div data-testid="admin-open-houses">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-light text-[#F5F5F0]" style={{ fontFamily: 'Playfair Display' }}>
              Open Houses
            </h1>
            <p className="text-[#808080] mt-1">Manage open house events</p>
          </div>
          <Button className="btn-gold" onClick={() => openEditModal()} data-testid="add-open-house-btn">
            <Plus className="w-4 h-4 mr-2" /> Add Open House
          </Button>
        </div>

        {/* Open Houses Grid */}
        {loading ? (
          <div className="flex justify-center py-16">
            <div className="spinner" />
          </div>
        ) : openHouses.length === 0 ? (
          <div className="dashboard-card text-center py-16">
            <p className="text-[#808080]">No open houses scheduled</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {openHouses.map((oh) => (
              <div key={oh.id} className="dashboard-card" data-testid={`open-house-card-${oh.id}`}>
                {oh.image_url && (
                  <img src={oh.image_url} alt={oh.property_address} className="w-full h-40 object-cover rounded-lg mb-4" />
                )}
                <div className="flex items-start justify-between mb-2">
                  <h3 className="text-[#F5F5F0] font-medium">{oh.property_address}</h3>
                  <div className="flex space-x-1">
                    <button 
                      onClick={() => handleGenerateQR(oh)}
                      className="p-2 hover:bg-blue-500/10 rounded text-blue-400"
                      title="Generate QR Code"
                    >
                      <QrCode className="w-4 h-4" />
                    </button>
                    <button 
                      onClick={() => openEditModal(oh)}
                      className="p-2 hover:bg-[#D4AF37]/10 rounded text-[#D4AF37]"
                      title="Edit"
                    >
                      <Edit2 className="w-4 h-4" />
                    </button>
                    <button 
                      onClick={() => handleDelete(oh.id)}
                      className="p-2 hover:bg-red-500/10 rounded text-red-400"
                      title="Delete"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
                
                <div className="space-y-2 text-sm">
                  <div className="flex items-center text-[#808080]">
                    <MapPin className="w-4 h-4 mr-2 text-[#D4AF37]" />
                    {oh.city}, {oh.state} {oh.zip_code}
                  </div>
                  <div className="flex items-center text-[#F5F5F0]">
                    <Calendar className="w-4 h-4 mr-2 text-[#D4AF37]" />
                    {oh.date}
                  </div>
                  <div className="flex items-center text-[#F5F5F0]">
                    <Clock className="w-4 h-4 mr-2 text-[#D4AF37]" />
                    {oh.start_time} - {oh.end_time}
                  </div>
                  {oh.agent_name && (
                    <p className="text-[#808080]">Host: {oh.agent_name}</p>
                  )}
                </div>
                
                {!oh.is_active && (
                  <span className="mt-3 inline-block text-xs px-2 py-1 bg-red-500/10 text-red-400 rounded">
                    Inactive
                  </span>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Edit/Add Modal */}
        {editingOH !== null && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4">
            <div className="bg-[#111111] p-8 rounded-lg max-w-lg w-full max-h-[90vh] overflow-y-auto border border-[#D4AF37]/20">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-xl font-light text-[#F5F5F0]" style={{ fontFamily: 'Playfair Display' }}>
                  {editingOH.id ? 'Edit Open House' : 'Add Open House'}
                </h3>
                <button onClick={() => setEditingOH(null)} className="text-[#808080] hover:text-[#F5F5F0]">
                  <X className="w-5 h-5" />
                </button>
              </div>
              
              <div className="space-y-4">
                <Input
                  placeholder="Property Address *"
                  value={formData.property_address}
                  onChange={(e) => setFormData({ ...formData, property_address: e.target.value })}
                  className="bg-[#1A1A1A] border-[#333] text-[#F5F5F0]"
                />
                <div className="grid grid-cols-3 gap-2">
                  <Input
                    placeholder="City *"
                    value={formData.city}
                    onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                    className="bg-[#1A1A1A] border-[#333] text-[#F5F5F0] col-span-2"
                  />
                  <Input
                    placeholder="State"
                    value={formData.state}
                    onChange={(e) => setFormData({ ...formData, state: e.target.value })}
                    className="bg-[#1A1A1A] border-[#333] text-[#F5F5F0]"
                  />
                </div>
                <Input
                  placeholder="ZIP Code *"
                  value={formData.zip_code}
                  onChange={(e) => setFormData({ ...formData, zip_code: e.target.value })}
                  className="bg-[#1A1A1A] border-[#333] text-[#F5F5F0]"
                />
                <Input
                  type="date"
                  placeholder="Date *"
                  value={formData.date}
                  onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                  className="bg-[#1A1A1A] border-[#333] text-[#F5F5F0]"
                />
                <div className="grid grid-cols-2 gap-2">
                  <Input
                    type="time"
                    placeholder="Start Time *"
                    value={formData.start_time}
                    onChange={(e) => setFormData({ ...formData, start_time: e.target.value })}
                    className="bg-[#1A1A1A] border-[#333] text-[#F5F5F0]"
                  />
                  <Input
                    type="time"
                    placeholder="End Time *"
                    value={formData.end_time}
                    onChange={(e) => setFormData({ ...formData, end_time: e.target.value })}
                    className="bg-[#1A1A1A] border-[#333] text-[#F5F5F0]"
                  />
                </div>
                <Input
                  placeholder="Host Agent Name"
                  value={formData.agent_name}
                  onChange={(e) => setFormData({ ...formData, agent_name: e.target.value })}
                  className="bg-[#1A1A1A] border-[#333] text-[#F5F5F0]"
                />
                <Textarea
                  placeholder="Description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={3}
                  className="bg-[#1A1A1A] border-[#333] text-[#F5F5F0]"
                />
                <Input
                  placeholder="Image URL"
                  value={formData.image_url}
                  onChange={(e) => setFormData({ ...formData, image_url: e.target.value })}
                  className="bg-[#1A1A1A] border-[#333] text-[#F5F5F0]"
                />
                {editingOH.id && (
                  <label className="flex items-center text-[#F5F5F0]">
                    <input
                      type="checkbox"
                      checked={formData.is_active}
                      onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                      className="mr-2"
                    />
                    Active
                  </label>
                )}
                <Button onClick={handleSave} className="btn-gold w-full" data-testid="save-open-house-btn">
                  Save Open House
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* QR Code Modal */}
        {qrModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4">
            <div className="bg-[#111111] p-8 rounded-lg max-w-md w-full border border-[#D4AF37]/20 text-center">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-xl font-light text-[#F5F5F0]" style={{ fontFamily: 'Playfair Display' }}>
                  QR Code for Sign-In
                </h3>
                <button onClick={() => setQrModal(null)} className="text-[#808080] hover:text-[#F5F5F0]">
                  <X className="w-5 h-5" />
                </button>
              </div>
              
              <p className="text-[#808080] text-sm mb-4">
                {qrModal.property_address}, {qrModal.city}
              </p>
              
              {qrModal.qr_code && (
                <div className="bg-white p-4 rounded-lg inline-block mb-4">
                  <img 
                    src={qrModal.qr_code} 
                    alt="QR Code" 
                    className="w-48 h-48"
                  />
                </div>
              )}
              
              <p className="text-[#808080] text-xs mb-4">
                Guests can scan this code to register for the open house
              </p>
              
              <div className="flex gap-3">
                <Button onClick={handleDownloadQR} className="btn-gold flex-1">
                  <Download className="w-4 h-4 mr-2" /> Download
                </Button>
                <Button 
                  variant="outline" 
                  className="btn-outline-gold flex-1"
                  onClick={() => {
                    navigator.clipboard.writeText(qrModal.sign_in_url);
                    toast.success('URL copied to clipboard');
                  }}
                >
                  Copy URL
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};
